import Button from '@/shared/Button';
import Div from '@/shared/Div';
import Divider from '@/shared/Divider';
import Input from '@/shared/Input';
import Paragraph from '@/shared/Paragraph';
import Form from '@/shared/Form';
import Map from '../Map';
import { inputOnlyNumbers, validatePinCode } from '@/utils/formValidation';
import React, { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { inventoryStatus } from '@/actions/ecom/checkInventoryStatus';
import useTriggerRequest from '@/hooks/useTriggerRequest';
import { EFL_PINCODE, EXISTING_PREXO_DETAILS } from '@/constants/app';
import { EcomGTMEventNames } from '@/types/ecomGTM';
import useEcomGTM from '@/hooks/useEcomGTM';
import useOnMount from '@/hooks/useOnMount';
import LocalStorage from '@/utils/localStorage';
import Span from '@/shared/Span';
import { prexoLearnMoreData } from '@/components/Product/ExchangeProduct';
import CheckCircle from '@/components/Icons/CheckCircle';
import FailedIcon from '@/components/Icons/FailedIcon';
import SuccessIcon from '@/components/Icons/SuccessIcon';
import Link from '@/shared/Link';
import useRoutingLoader from '@/hooks/useRoutingLoader';
import MulticategoryPrexoContent from '@/components/Product/MulticategoryPrexoContent';
import Image from '@/shared/Image';
import LeftArrowIcon from '../Icons/LeftArrow';
type FormInputs = {
  pinCode: string;
};

type ExchangeOldProduct = {
  onConfirm: () => void;
  onClose: () => void;
  productId: string;
  prexoDiscount: string;
  prexoValues: any;
  categoryType?: string;
  handleAddToCart?: (() => void) | (() => Promise<void>);
  onSelectPrexoName?: (value: { categoryName: string; value: number }) => void;
};

interface PincodeStatus {
  status: 'pending' | 'success' | 'failed' | 'pincode-exists';
  data: any;
  pinCode: string | null;
}

enum PrexoState {
  mainState = 'entryState',
  successState = 'successState',
  multicategoryState = 'multicategoryState',
}

function ExchangeOldProduct(props: ExchangeOldProduct) {
  const {
    onConfirm,
    productId,
    prexoDiscount,
    prexoValues,
    onClose,
    categoryType,
    handleAddToCart,
    onSelectPrexoName,
  } = props;
  const eflPincode = LocalStorage.get(EFL_PINCODE);

  const [pincodeStatus, setPincodeStatus] = useState<PincodeStatus>({
    status: eflPincode ? 'pincode-exists' : 'pending',
    data: null,
    pinCode: eflPincode ?? null,
  });
  const { fireEcomGTMEvent } = useEcomGTM();
  const [selectedState, setSelectedState] = useState<PrexoState>(PrexoState.multicategoryState);
  const [showAllCategories, setShowAllCategories] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<number | null>(null);
  const { routing, setRouting } = useRoutingLoader();

  useEffect(() => {
    if (selectedCategory !== null && prexoValues && prexoValues[selectedCategory]) {
      onSelectPrexoName?.({
        categoryName: prexoValues[selectedCategory].categoryName,
        value: prexoValues[selectedCategory].value,
      });
      LocalStorage.set(EXISTING_PREXO_DETAILS, {
        prexoExchangeCategoryName: prexoValues[selectedCategory].categoryName,
        prexoExchangeMRP: prexoValues[selectedCategory].value,
      });
    }
  }, [selectedCategory, prexoValues, onSelectPrexoName]);

  const {
    handleSubmit,
    register,
    formState: { errors },
  } = useForm<FormInputs>({
    defaultValues: {
      pinCode: eflPincode ?? '',
    },
  });

  const fetch = (...args: any) => inventoryStatus.apply(null, args);
  const {
    data: inventoryData,
    isLoading,
    promisifyTrigger,
    isError,
  } = useTriggerRequest({ fetch });

  const checkPrexoAvailability = (responseData: any) => {
    if (responseData?.data?.data?.prexoAvailable) {
      setPincodeStatus({ status: 'success', data: responseData, pinCode: eflPincode });
    } else {
      setPincodeStatus({ status: 'failed', data: null, pinCode: null });
    }
  };

  useOnMount(async () => {
    if (eflPincode) {
      const productData = { productIds: [productId], pinCode: eflPincode };
      let responseData = await promisifyTrigger(productData);
      checkPrexoAvailability(responseData);
    }
  });

  const onSubmit = useCallback(
    async (data: FormInputs) => {
      const { pinCode } = data;
      const productData = { productIds: [productId], pinCode: pinCode };
      let responseData = await promisifyTrigger(productData);
      checkPrexoAvailability(responseData);
    },
    [productId, promisifyTrigger, pincodeStatus],
  );

  const renderStatus = useCallback(() => {
    switch (pincodeStatus.status) {
      case 'pending':
        return 'Check if exchange is applicable in your area';
      case 'success':
        return 'Exchange applicable';
      case 'failed':
        return 'Exchange not applicable at this pincode';
      case 'pincode-exists':
        return 'Checking...';
      default:
        return 'Check if exchange is applicable in your area';
    }
  }, [pincodeStatus.status]);

  const onClickHandler = () => {
    if (selectedState === PrexoState.multicategoryState) {
      setSelectedState(PrexoState.mainState);
    } else if (selectedState === PrexoState.mainState) {
      onConfirmClick();
    } else {
      if (handleAddToCart) {
        setRouting(true);
        Promise.resolve(handleAddToCart()).finally(() => setRouting(false));
      }
    }
  };

  const onConfirmClick = useCallback(() => {
    fireEcomGTMEvent(EcomGTMEventNames.PDP.PREXO_SELECTION_COMPLETED, {
      is_pin_code_prexo_serviceable: pincodeStatus.data?.data?.data?.prexoAvailable ?? false,
      is_prexo_available: true,
      pincode_value: pincodeStatus.pinCode,
    });
    onConfirm();
    setSelectedState(PrexoState.successState);
  }, [pincodeStatus.status]);

  const mainContent = () => {
    // Get selected values
    const selectedPrexoValue =
      selectedCategory !== null && prexoValues && prexoValues[selectedCategory]
        ? prexoValues[selectedCategory].value
        : 0;
    const selectedPrexoName =
      selectedCategory !== null && prexoValues && prexoValues[selectedCategory]
        ? prexoValues[selectedCategory].categoryName
        : '';

    return (
      <Div>
        <Div className="flex flex-row items-center gap-s3 mx-s5 md:mb-s7">
          <Button
            theme="none"
            className=""
            onClick={() => setSelectedState(PrexoState.multicategoryState)}
          >
            <LeftArrowIcon />
          </Button>
          <Paragraph className=" text-cti-primary text-h-3 ">Check Exchange Eligibility</Paragraph>
        </Div>
        <Paragraph className="mx-s5 text-cti-primary text-body-lg mt-s3">
          {`Your exchange item will be verified at the doorstep by our delivery agent. `}
          <Span>
            <Link target="_blank" className="text-blue-400" href={'/faqs-product-exchange-offer'}>
              Learn more
            </Link>
          </Span>
        </Paragraph>
        <Div
          className="flex p-s5 items-center gap-s4 rounded-b-cs bg-cs-secondary mx-s5 mt-s5 "
          style={{ alignSelf: 'stretch' }}
        >
          {/* Show selectedPrexoImage if available */}
          {selectedCategory !== null && prexoValues && prexoValues[selectedCategory]?.imageUrl && (
            <Image
              src={prexoValues[selectedCategory].imageUrl}
              alt={selectedPrexoName}
              className="w-s6 h-s6 object-contain"
            />
          )}
          <Paragraph className="flex-1 text-callout-normal ">{selectedPrexoName}</Paragraph>
          <Paragraph className="text-cu-positive text-callout-normal">
            {selectedPrexoValue ? `₹${selectedPrexoValue} OFF` : ''}
          </Paragraph>
        </Div>
        <Divider className="mt-s5 w-full" />
        <Div className="p-s5 mx-s5 mt-s5">
          <Form
            className="flex justify-between items-start gap-x-s4 py-s3"
            onSubmit={handleSubmit(onSubmit)}
          >
            <Div className="text-body-lg flex-grow">
              <Input
                type="text"
                size="xs"
                className="pb-s3 border-0 border-b border-co-bold text-body-lg text-cti-tertiary rounded-none !bg-transparent !outline-none !focus:outline-none w-full"
                maxLength={6}
                {...register('pinCode', {
                  validate: {
                    validatePinCode,
                  },
                })}
                placeholder="Enter Pincode"
                onKeyPress={inputOnlyNumbers}
              />
            </Div>
            <Button
              theme="secondary"
              size="sm"
              className="py-s3 px-s4 rounded-cxs !body-base text-cti-primary"
              type="submit"
            >
              Check
            </Button>
          </Form>
          <Div className="flex flex-row gap-s2 items-center">
            {pincodeStatus.status === 'success' && (
              <Span>
                <SuccessIcon />
              </Span>
            )}
            {pincodeStatus.status === 'failed' && (
              <Span>
                <FailedIcon />
              </Span>
            )}
            <Paragraph className="text-cti-primary">{renderStatus()}</Paragraph>
          </Div>
        </Div>
      </Div>
    );
  };

  const infoContent = () => {
    return (
      <Div>
        <Paragraph className="mx-s5 text-cti-primary mb-s5 text-h-3">
          Important Information
        </Paragraph>
        <Map
          className="flex flex-col gap-s6 mt-s6 mx-s5"
          list={prexoLearnMoreData.infoStepsList}
          wrapper="div"
          render={(item, index) => {
            return (
              <Div className="flex gap-s5" key={index}>
                <Div>
                  {' '}
                  <Div className="w-8 h-8 border border-co-bold rounded-3xl	flex justify-center items-center text-h-4">
                    {index + 1}
                  </Div>
                </Div>
                <Div>
                  <Paragraph className="text-h-4">{item.title}</Paragraph>
                  <Paragraph className="body-base mt-s2">{item.subTitle}</Paragraph>
                </Div>
              </Div>
            );
          }}
        />
        <Div className="m-s5 px-s5 py-s4 rounded-cs bg-[#E5F1FF]">
          <Paragraph className="text-cm-primary text-body-lg">
            If there is a product mis-match, your order will be cancelled.
          </Paragraph>
        </Div>
      </Div>
    );
  };

  const successContent = () => {
    const selectedPrexoValue = selectedCategory !== null ? prexoValues[selectedCategory].value : 0;
    const selectedPrexoName =
      selectedCategory !== null ? prexoValues[selectedCategory].categoryName : '';
    const selectedPrexoImage =
      selectedCategory !== null ? prexoValues[selectedCategory].imageUrl : '';
    return (
      <Div className="p-s5 items-center flex flex-col">
        <CheckCircle />
        <Paragraph className="text-hd-3 mt-s5 mb-s3 text-cm-primary">Exchange applied!</Paragraph>
        <Paragraph className="text-body-xl text-cti-secondary mt-s3">
          You <Span className="text-blue-400">{`saved ₹${selectedPrexoValue}`}</Span> on this order
        </Paragraph>
      </Div>
    );
  };

  const renderContent = () => {
    switch (selectedState) {
      case PrexoState.mainState:
        return mainContent();
      case PrexoState.multicategoryState:
        return (
          <MulticategoryPrexoContent
            prexoValues={prexoValues}
            showAllCategories={showAllCategories}
            setShowAllCategories={setShowAllCategories}
            selectedCategory={selectedCategory}
            setSelectedCategory={setSelectedCategory}
            setSelectedState={setSelectedState}
            PrexoState={PrexoState}
          />
        );
      case PrexoState.successState:
        return successContent();
    }
  };

  return (
    <Div className="flex flex-col h-full">
      <Div className="overflow-auto max-h-[calc(100vh-16rem)] md:max-h-[calc(100vh-9rem)] mb-s5">
        {renderContent()}
      </Div>
      <Div className="m-s5">
        {selectedState === PrexoState.mainState && (
          <Button
            theme={'primary'}
            className={'w-full'}
            dataTestId={'confirm-button'}
            onClick={onClickHandler}
            disabled={pincodeStatus.status !== 'success'}
            isLoading={false}
          >
            Apply Discount
          </Button>
        )}
        {selectedState === PrexoState.multicategoryState && (
          <Button
            theme={'primary'}
            className={'w-full'}
            dataTestId={'confirm-button'}
            disabled={selectedCategory === null}
            onClick={onClickHandler}
            isLoading={false}
          >
            Continue with Exchange
          </Button>
        )}
        {selectedState === PrexoState.successState && (
          <Button
            theme={'primary'}
            className={'w-full'}
            dataTestId={'confirm-button'}
            onClick={onClickHandler}
            isLoading={routing}
          >
            Add to Cart With Exchange
          </Button>
        )}
      </Div>
    </Div>
  );
}

export default React.memo(ExchangeOldProduct);
